import React, { FC } from "react";
import { Box, Grid, IconButton, Tooltip, Typography } from "@mui/material";
import { Info } from "feather-icons-react";
import { useTranslation } from "react-i18next";

import { IncomeQuartileData } from "../../hooks";

import { useGetPaygapSettings } from "@api/hooks/paygap";
import { useUserStore } from "@store/user";
import {
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@components/base";
import { BoxPlot, CircularGauge } from "@components/common/charts";

interface PaygapIncomeQuartileAnalysisProps {
  incomeQuartileData: IncomeQuartileData[];
  unadjustedPayGap: number;
}

const PaygapIncomeQuartileAnalysis: FC<PaygapIncomeQuartileAnalysisProps> = ({
  incomeQuartileData,
  unadjustedPayGap,
}) => {
  const { t } = useTranslation();

  // Get user for API hook
  const { user } = useUserStore();
  const { settings } = useGetPaygapSettings(user, true);

  // Resolve currency code from API settings
  const currencyCode = settings?.analysisParameters?.currency || "EUR";

  // Helper function to format values
  const formatValue = (
    value: string,
    isPercentage: boolean = false,
    isCurrency: boolean = false,
  ): string => {
    if (
      value === "#DIV/0!" ||
      value === "#VALUE!" ||
      value === "#N/A" ||
      !value ||
      value === "null"
    )
      return "N/A";
    const num = parseFloat(value);

    if (isNaN(num)) return "N/A";

    if (isCurrency) {
      try {
        return new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: currencyCode,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(num);
      } catch {
        return new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "EUR",
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(num);
      }
    }

    if (isPercentage) {
      return `${num.toFixed(1)}%`;
    }

    return num.toLocaleString();
  };

  // Helper to find data by translation key match
  const findByMetricKey = (translationKey: string): IncomeQuartileData | undefined => {
    const translatedMetric = t(translationKey);


return incomeQuartileData.find((item) => item.metric === translatedMetric);
  };

  // Check if metric should show CZ comparison
  const shouldShowCzComparison = (metric: string): boolean => {
    const minimumMetric = t("paygap.data.quartile.minimum");
    const rangeMetric = t("paygap.data.quartile.range");


return metric !== minimumMetric && metric !== rangeMetric;
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {t("paygap.analysis.incomeQuartile.title")}
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t("paygap.analysis.incomeQuartile.metric")}</TableCell>
                  <TableCell align="right">{`${t("paygap.analysis.incomeQuartile.men")} (${currencyCode})`}</TableCell>
                  <TableCell align="right">{`${t("paygap.analysis.incomeQuartile.women")} (${currencyCode})`}</TableCell>
                  <TableCell align="right">{t("paygap.analysis.incomeQuartile.czComparison")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {incomeQuartileData.map((row) => (
                  <TableRow key={row.metric}>
                    <TableCell>{row.metric}</TableCell>
                    <TableCell align="right">
                      {formatValue(row.men.toString(), false, row.isCurrency !== false)}
                    </TableCell>
                    <TableCell align="right">
                      {formatValue(row.women.toString(), false, row.isCurrency !== false)}
                    </TableCell>
                    <TableCell align="right">
                      {!shouldShowCzComparison(row.metric)
                        ? "—"
                        : row.czComparison !== undefined
                          ? formatValue(row.czComparison.toString(), true)
                          : "N/A"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box id="paygap-income-quartile-boxplot" display="flex" flexDirection="column" gap={1.5} alignItems="center">
              {/* Unadjusted Gender Pay Difference Gauge */}
              <Box
                width="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap={1}
                p={1}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="subtitle2">
                    {t("paygap.analysis.incomeQuartile.unadjustedTitle")}
                  </Typography>
                  <Tooltip
                    title={t("paygap.analysis.incomeQuartile.unadjustedTooltip")}
                  >
                    <IconButton size="small">
                      <Info size={14} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <CircularGauge value={Math.abs(unadjustedPayGap)} />
                <Typography variant="body2" fontWeight={600}>
                  {unadjustedPayGap.toFixed(1)}%{" — "}
                  {unadjustedPayGap === 0
                    ? t("paygap.analysis.incomeQuartile.unadjustedNoDiff")
                    : unadjustedPayGap > 0
                      ? t("paygap.analysis.incomeQuartile.unadjustedMenLess")
                      : t("paygap.analysis.incomeQuartile.unadjustedWomenLess")}
                </Typography>
              </Box>

              {/* Income Distribution Box Plot */}
              <BoxPlot
                title={`${t("paygap.graph.incomeDistribution.title", "Income Distribution")} (${currencyCode})`}
                currencyCode={currencyCode}
                series={[
                  {
                    data: {
                      min: findByMetricKey("paygap.data.quartile.minimum")?.men || 0,
                      q1: findByMetricKey("paygap.data.quartile.q1")?.men || 0,
                      median: findByMetricKey("paygap.data.quartile.q2")?.men || 0,
                      q3: findByMetricKey("paygap.data.quartile.q3")?.men || 0,
                      max: findByMetricKey("paygap.data.quartile.q4")?.men || 0,
                    },
                    label: t("paygap.analysis.incomeQuartile.men", "Men"),
                    color: "#2196f3",
                  },
                  {
                    data: {
                      min: findByMetricKey("paygap.data.quartile.minimum")?.women || 0,
                      q1: findByMetricKey("paygap.data.quartile.q1")?.women || 0,
                      median: findByMetricKey("paygap.data.quartile.q2")?.women || 0,
                      q3: findByMetricKey("paygap.data.quartile.q3")?.women || 0,
                      max: findByMetricKey("paygap.data.quartile.q4")?.women || 0,
                    },
                    label: t("paygap.analysis.incomeQuartile.women", "Women"),
                    color: "#9c27b0",
                  },
                ]}
              />
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default PaygapIncomeQuartileAnalysis;
